import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, ArrowUpDown } from 'lucide-react';

/**
 * LP Performance Center — landing screen.
 *
 * Aimed at LP allocators (institutional and family-office) evaluating PE /
 * private credit fund performance. Three panels:
 *   1. KPI strip — tenant composite IRR / MOIC / DPI / KS-PME
 *   2. Cohort heatmap — vintage × fundType (or strategy) grid
 *   3. Fund table — every fund the tenant holds, sortable, drilldown to detail
 *
 * Endpoints:
 *   GET /api/pe/performance/composite
 *   GET /api/pe/performance/funds
 *   GET /api/pe/performance/cohorts?dimension=fundType|strategy
 */

interface Metrics {
  scope: string;
  scopeId: number | null;
  label: string;
  fundType?: string | null;
  vintageYear?: number | null;
  manager?: string | null;
  strategy?: string | null;
  fundCount?: number | null;
  committed: number;
  called: number;
  distributed: number;
  reportedNav: number;
  totalValue: number;
  unfunded: number;
  irr?: number | null;
  moic?: number | null;
  tvpi?: number | null;
  dpi?: number | null;
  rvpi?: number | null;
  ksPme?: number | null;
  directAlpha?: number | null;
  benchmarkIRR?: number | null;
  benchmarkCode?: string;
  navAsOf?: string | null;
  firstFlowDate?: string | null;
  lastFlowDate?: string | null;
  cashFlowCount?: number | null;
}

interface CohortCell {
  vintage: number;
  dimension: string;
  fundCount: number;
  committed: number;
  called: number;
  distributed: number;
  nav: number;
  irr: number | null;
  moic: number | null;
  dpi: number | null;
  tvpi: number | null;
  ksPme: number | null;
  directAlpha: number | null;
}

interface CohortMatrix {
  dimension: string;
  benchmarkCode: string;
  cells: CohortCell[];
}

// ---------- formatters ----------
const fmtMoney = (v?: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};
const fmtPct = (v?: number | null, d = 1) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const fmtMult = (v?: number | null) => (v == null ? '—' : `${v.toFixed(2)}x`);
const fmtDate = (iso?: string | null) => (iso ? iso : '—');

// IRR-tone heuristic: ≥ 15% green, ≥ 8% amber, < 8% red
function ircTone(irr: number | null | undefined): string {
  if (irr == null) return 'bg-slate-800/40';
  if (irr >= 0.15) return 'bg-emerald-950/40 border-emerald-800/30';
  if (irr >= 0.08) return 'bg-amber-950/40 border-amber-800/30';
  return 'bg-red-950/40 border-red-800/30';
}

function irrTextTone(irr: number | null | undefined): string {
  if (irr == null) return 'text-slate-500';
  if (irr >= 0.15) return 'text-emerald-300';
  if (irr >= 0.08) return 'text-amber-300';
  return 'text-red-300';
}

type SortKey = 'label' | 'vintageYear' | 'committed' | 'irr' | 'moic' | 'dpi' | 'ksPme';

const Kpi: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'positive' | 'warning';
}> = ({ label, value, sub, tone = 'neutral' }) => {
  const color =
    tone === 'positive' ? 'text-emerald-300' : tone === 'warning' ? 'text-amber-300' : 'text-white';
  return (
    <div className="bg-slate-900/40 border border-white/5 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const LPPerformanceCenterScreen: React.FC = () => {
  const navigate = useNavigate();
  const [composite, setComposite] = useState<Metrics | null>(null);
  const [funds, setFunds] = useState<Metrics[]>([]);
  const [cohorts, setCohorts] = useState<CohortMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dimension, setDimension] = useState<'fundType' | 'strategy'>('fundType');
  const [sortKey, setSortKey] = useState<SortKey>('committed');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [c, f, h] = await Promise.all([
          axios.get<Metrics>('/api/pe/performance/composite'),
          axios.get<Metrics[]>('/api/pe/performance/funds'),
          axios.get<CohortMatrix>(`/api/pe/performance/cohorts?dimension=${dimension}`),
        ]);
        if (cancelled) return;
        setComposite(c.data);
        setFunds(Array.isArray(f.data) ? f.data : []);
        setCohorts(h.data);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dimension]);

  const sortedFunds = useMemo(() => {
    const copy = [...funds];
    copy.sort((a, b) => {
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [funds, sortKey, sortDir]);

  const cohortGrid = useMemo(() => {
    if (!cohorts) return null;
    const vintages = Array.from(new Set(cohorts.cells.map((c) => c.vintage))).sort((a, b) => a - b);
    const dims = Array.from(new Set(cohorts.cells.map((c) => c.dimension))).sort();
    const lookup = new Map<string, CohortCell>();
    cohorts.cells.forEach((c) => lookup.set(`${c.vintage}|${c.dimension}`, c));
    return { vintages, dims, lookup };
  }, [cohorts]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  const sortIcon = (k: SortKey) =>
    sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div className="px-6 py-6 text-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-violet-400" />
            LP Performance Center
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Fund and portfolio performance derived from real cash flows, reported NAVs, and
            daily-refreshed public-market benchmarks. KS-PME and Direct Alpha alongside IRR and MOIC.
          </p>
        </div>
      </div>

      {loading && (
        <div className="px-5 py-12 text-center text-slate-500 text-sm">Loading performance data…</div>
      )}
      {error && !loading && (
        <div className="px-5 py-12 text-center text-red-400 text-sm">{error}</div>
      )}

      {!loading && !error && composite && (
        <>
          {/* KPI Strip */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <Kpi
              label="Committed"
              value={fmtMoney(composite.committed)}
              sub={`${composite.fundCount ?? 0} fund${composite.fundCount === 1 ? '' : 's'} · ${fmtMoney(composite.unfunded)} unfunded`}
            />
            <Kpi
              label="Net IRR"
              value={fmtPct(composite.irr)}
              sub={`Benchmark ${composite.benchmarkCode ?? ''} ${fmtPct(composite.benchmarkIRR)}`}
              tone={composite.irr != null && composite.irr >= 0 ? 'positive' : 'warning'}
            />
            <Kpi
              label="TVPI / DPI"
              value={`${fmtMult(composite.tvpi)} / ${fmtMult(composite.dpi)}`}
              sub={`RVPI ${fmtMult(composite.rvpi)}`}
            />
            <Kpi
              label="KS-PME · Direct Alpha"
              value={`${fmtMult(composite.ksPme)} · ${fmtPct(composite.directAlpha)}`}
              sub={`vs ${composite.benchmarkCode ?? ''} · NAV as of ${fmtDate(composite.navAsOf)}`}
            />
          </div>

          {/* Cohort Heatmap */}
          <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
              <h3 className="text-sm font-semibold text-white">
                Cohort matrix — vintage × {dimension === 'strategy' ? 'strategy' : 'fund type'}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setDimension('fundType')}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                    dimension === 'fundType'
                      ? 'bg-violet-600/30 border-violet-500/50 text-violet-300'
                      : 'bg-transparent border-white/10 text-slate-400 hover:border-violet-500/30'
                  }`}
                >
                  Fund type
                </button>
                <button
                  onClick={() => setDimension('strategy')}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                    dimension === 'strategy'
                      ? 'bg-violet-600/30 border-violet-500/50 text-violet-300'
                      : 'bg-transparent border-white/10 text-slate-400 hover:border-violet-500/30'
                  }`}
                >
                  Strategy
                </button>
              </div>
            </div>

            <div className="p-4">
              {!cohortGrid || cohortGrid.vintages.length === 0 ? (
                <div className="px-5 py-8 text-center text-slate-500 text-sm">
                  No cohort data — upload commitments with a vintage year to populate.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                        <th className="text-left px-3 py-2 font-semibold w-20">Vintage</th>
                        {cohortGrid.dims.map((d) => (
                          <th key={d} className="text-left px-3 py-2 font-semibold">{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cohortGrid.vintages.map((vy) => (
                        <tr key={vy} className="border-t border-white/5">
                          <td className="px-3 py-2 font-bold text-slate-300">{vy}</td>
                          {cohortGrid.dims.map((d) => {
                            const cell = cohortGrid.lookup.get(`${vy}|${d}`);
                            if (!cell) {
                              return (
                                <td key={d} className="px-3 py-2 text-slate-600">—</td>
                              );
                            }
                            return (
                              <td key={d} className="px-2 py-1">
                                <div className={`rounded-md border px-3 py-2 ${ircTone(cell.irr)}`}>
                                  <div className={`text-sm font-bold font-mono ${irrTextTone(cell.irr)}`}>
                                    {fmtPct(cell.irr)}
                                  </div>
                                  <div className="text-[10px] text-slate-400 mt-0.5">
                                    {fmtMult(cell.moic)} · KS-PME {fmtMult(cell.ksPme)} · {cell.fundCount} fund{cell.fundCount === 1 ? '' : 's'}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Funds Table */}
          <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
              <h3 className="text-sm font-semibold text-white">Funds</h3>
              <span className="text-xs text-slate-500">{sortedFunds.length} held</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-950/40">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-5 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('label')}>
                      Fund{sortIcon('label')}
                    </th>
                    <th className="text-left px-3 py-2 font-semibold">Type</th>
                    <th className="text-left px-3 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('vintageYear')}>
                      Vintage{sortIcon('vintageYear')}
                    </th>
                    <th className="text-left px-3 py-2 font-semibold">Manager</th>
                    <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('committed')}>
                      Committed{sortIcon('committed')}
                    </th>
                    <th className="text-right px-3 py-2 font-semibold">Called</th>
                    <th className="text-right px-3 py-2 font-semibold">Distributed</th>
                    <th className="text-right px-3 py-2 font-semibold">NAV</th>
                    <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('irr')}>
                      IRR{sortIcon('irr')}
                    </th>
                    <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('moic')}>
                      MOIC{sortIcon('moic')}
                    </th>
                    <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('dpi')}>
                      DPI{sortIcon('dpi')}
                    </th>
                    <th className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-slate-300" onClick={() => onSort('ksPme')}>
                      KS-PME{sortIcon('ksPme')}
                    </th>
                    <th className="text-right px-5 py-2 font-semibold">Direct α</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFunds.length === 0 && (
                    <tr>
                      <td colSpan={13} className="px-5 py-12 text-center text-slate-500 text-sm">
                        No funds held — onboard a commitment to see performance.
                      </td>
                    </tr>
                  )}
                  {sortedFunds.map((f) => (
                    <tr
                      key={f.scopeId ?? f.label}
                      onClick={() => f.scopeId && navigate(`/portfolio-management?tab=alternatives&sub=lp-fund-detail&fundId=${f.scopeId}`)}
                      className="border-t border-white/5 hover:bg-white/5 transition-colors"
                      style={{ cursor: f.scopeId ? 'pointer' : 'default' }}
                    >
                      <td className="px-5 py-2.5 text-slate-200 font-medium">{f.label}</td>
                      <td className="px-3 py-2.5 text-slate-400">{f.fundType ?? '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-slate-300">{f.vintageYear ?? '—'}</td>
                      <td className="px-3 py-2.5 text-slate-400">{f.manager ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-200">{fmtMoney(f.committed)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-400">{fmtMoney(f.called)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-400">{fmtMoney(f.distributed)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-400">{fmtMoney(f.reportedNav)}</td>
                      <td className={`px-3 py-2.5 text-right font-mono font-semibold ${irrTextTone(f.irr)}`}>{fmtPct(f.irr)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-300">{fmtMult(f.moic)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-300">{fmtMult(f.dpi)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-300">{fmtMult(f.ksPme)}</td>
                      <td className="px-5 py-2.5 text-right font-mono text-slate-300">{fmtPct(f.directAlpha)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LPPerformanceCenterScreen;
